// Import delle dipendenze necessarie da React e React Router.
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Import delle dipendenze da Redux per interagire con lo stato globale.
import { useDispatch, useSelector } from 'react-redux';
// Import dell'azione asincrona (thunk) per la registrazione.
import { registerUser } from '../redux/authSlice';
// Import di componenti riutilizzabili.
import Navbar from '../components/Navbar';
import './Auth.css';

// Definizione del componente funzionale Register.
export default function Register() {
    // Stato per memorizzare i dati del form (nome, email, password).
    const [formData, setFormData] = useState({
        nome: '', // Corrisponde al campo 'nome' richiesto dal backend.
        email: '',
        password: '',
    });
    // Stato separato per il campo di conferma password.
    const [confirmPassword, setConfirmPassword] = useState('');
    // Stato per memorizzare gli errori di validazione del form.
    const [formErrors, setFormErrors] = useState({});

    // Hook di Redux per "dispatchare" (inviare) azioni.
    const dispatch = useDispatch();
    // Hook di React Router per navigare programmaticamente.
    const navigate = useNavigate();
    // Hook di Redux per selezionare dati dallo stato globale (loading e apiError).
    const { loading, error: apiError } = useSelector((state) => state.auth);

    // Gestore per l'aggiornamento dello stato quando l'utente scrive nei campi.
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // Funzione per validare i dati del form prima dell'invio.
    const validateForm = () => {
        const errors = {};
        if (!formData.nome) errors.nome = "Il nome è obbligatorio.";
        if (!formData.email) errors.email = "L'email è obbligatoria.";
        if (!formData.password) errors.password = "La password è obbligatoria.";
        if (formData.password.length < 8) errors.password = "La password deve essere di almeno 8 caratteri.";
        if (formData.password !== confirmPassword) errors.confirmPassword = "Le password non coincidono.";
        
        // Aggiorna lo stato degli errori, che causerà un re-render mostrando i messaggi.
        setFormErrors(errors);

        // Ritorna 'true' solo se non ci sono errori.
        return Object.keys(errors).length === 0;
    };

    // Gestore per l'invio del form.
    const handleSubmit = async (e) => {
        // Previene il comportamento predefinito del browser (ricaricare la pagina).
        e.preventDefault();
        
        // Blocca l'invio se la validazione fallisce.
        if (!validateForm()) {
            return;
        }

        try {
            // "Dispatcha" l'azione di registrazione con i dati del form.
            const resultAction = await dispatch(registerUser(formData));
            
            // Controlla se l'azione si è conclusa con successo (fulfilled).
            if (registerUser.fulfilled.match(resultAction)) {
                // Se la registrazione ha successo, reindirizza l'utente alla pagina di login.
                navigate('/login?registration=success');
            } else {
                 // L'errore viene già gestito dallo stato 'apiError' di Redux.
                 // Il console.error è utile per il debug.
                 console.error("Registrazione fallita:", resultAction.payload);
            }
        } catch (err) {
            console.error("Errore inatteso durante il dispatch:", err);
        }
    };

    // JSX che definisce la struttura del componente da renderizzare.
    return (
        <>
            <Navbar />
            <div className="auth-page-container">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-8 col-lg-6">
                            <div className="auth-card p-4 p-sm-5">
                                <h2 className="text-center fw-bold mb-4">Crea il tuo account</h2>
                                <p className="text-center text-muted mb-4">
                                    Entra a far parte di FlashMind. È semplice e veloce.
                                </p>
                                <form onSubmit={handleSubmit} noValidate>
                                    {/* Mostra un alert se c'è un errore dall'API. */}
                                    {apiError && <div className="alert alert-danger">{apiError}</div>}
                                    
                                    <div className="mb-3">
                                        <label htmlFor="name" className="form-label">Nome</label>
                                        <input
                                            type="text"
                                            // Aggiunge la classe 'is-invalid' se c'è un errore per questo campo.
                                            className={`form-control ${formErrors.nome ? 'is-invalid' : ''}`}
                                            id="name"
                                            name="nome"
                                            value={formData.nome}
                                            onChange={handleChange}
                                            required
                                        />
                                        {/* Mostra il messaggio di errore specifico per questo campo. */}
                                        {formErrors.nome && <div className="invalid-feedback">{formErrors.nome}</div>}
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="email" className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                        {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="password" className="form-label">Password</label>
                                        <input
                                            type="password"
                                            className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                        />
                                        {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                                    </div>
                                    <div className="mb-4">
                                        <label htmlFor="confirmPassword" className="form-label">Conferma password</label>
                                        <input
                                            type="password"
                                            className={`form-control ${formErrors.confirmPassword ? 'is-invalid' : ''}`}
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                        />
                                        {formErrors.confirmPassword && <div className="invalid-feedback">{formErrors.confirmPassword}</div>}
                                    </div>
                                    <div className="d-grid">
                                        {/* Disabilita il bottone durante il caricamento. */}
                                        <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                                            {loading ? 'Registrazione in corso...' : 'Registrati'}
                                        </button>
                                    </div>
                                </form>
                                <p className="text-center mt-4 mb-0">
                                    Hai già un account? <Link to="/login">Accedi qui</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}